import { useState, useCallback, useEffect } from 'react';
import { isAuthenticated } from './lib/auth';
import { Sidebar } from './components/layout/Sidebar';
import { Header } from './components/layout/Header';
import { LoginView } from './views/auth/LoginView';
import { VerifyView } from './views/auth/VerifyView';
import { DashboardView } from './views/DashboardView';
import { ContactsView } from './views/ContactsView';
import { ContactDetailView } from './views/ContactDetailView';
import { OrganizationsView } from './views/OrganizationsView';
import { DealsView } from './views/DealsView';
import { DealDetailView } from './views/DealDetailView';
import { TasksView } from './views/TasksView';
import { InteractionsView } from './views/InteractionsView';
import { ImportView } from './views/ImportView';
import { DossierView } from './views/DossierView';
import { ResearchView } from './views/ResearchView';
import { ReportsView } from './views/ReportsView';
import { WorkflowsView } from './views/WorkflowsView';
import { RenewalsView } from './views/RenewalsView';
import { CasesView } from './views/CasesView';
import { PlaybooksView } from './views/PlaybooksView';
import { EmailTemplatesView } from './views/EmailTemplatesView';
import { LeadsView } from './views/LeadsView';
import { ForecastingView } from './views/ForecastingView';
import { ProductsView } from './views/ProductsView';
import { QuotesView } from './views/QuotesView';
import { CampaignsView } from './views/CampaignsView';
import { SequencesView } from './views/SequencesView';
import { SubscriptionsView } from './views/SubscriptionsView';
import { InvoicesView } from './views/InvoicesView';
import { SettingsView } from './views/SettingsView';
import { ActivityQueueView } from './views/ActivityQueueView';
import { DataQualityView } from './views/DataQualityView';
import { TerritoriesView } from './views/TerritoriesView';
import { WebFormsView } from './views/WebFormsView';
import { WebhooksView } from './views/WebhooksView';

type ViewId =
  | 'dashboard'
  | 'contacts'
  | 'contact-detail'
  | 'organizations'
  | 'org-detail'
  | 'deals'
  | 'deal-detail'
  | 'interactions'
  | 'tasks'
  | 'agenda'
  | 'import'
  | 'tags'
  | 'team'
  | 'dossiers'
  | 'research'
  | 'reports'
  | 'workflows'
  | 'renewals'
  | 'cases'
  | 'playbooks'
  | 'email-templates'
  | 'leads'
  | 'forecasting'
  | 'products'
  | 'quotes'
  | 'campaigns'
  | 'sequences'
  | 'subscriptions'
  | 'invoices'
  | 'activity-queue'
  | 'data-quality'
  | 'territories'
  | 'web-forms'
  | 'webhooks'
  | 'search'
  | 'settings';

function PlaceholderView({ title }: { title: string }) {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
      </div>
      <div className="flex items-center justify-center p-16">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface-100">
            <span className="text-2xl text-slate-400">?</span>
          </div>
          <h2 className="text-lg font-semibold text-slate-700">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">This view is coming soon.</p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [currentView, setCurrentView] = useState<ViewId>('dashboard');
  const [viewParams, setViewParams] = useState<Record<string, string>>({});
  const [authed, setAuthed] = useState(isAuthenticated);

  // Check for verify token in URL
  const [verifyToken, setVerifyToken] = useState<string | null>(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('token');
  });

  // Re-check auth on storage changes
  useEffect(() => {
    function handleStorage() {
      setAuthed(isAuthenticated());
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleNavigate = useCallback((view: string, params?: Record<string, string>) => {
    setCurrentView(view as ViewId);
    setViewParams(params ?? {});
  }, []);

  const handleVerifySuccess = useCallback(() => {
    setVerifyToken(null);
    setAuthed(true);
    setCurrentView('dashboard');
    // Clean up the URL
    window.history.replaceState({}, '', '/');
  }, []);

  // Show verify view if token is in URL
  if (verifyToken) {
    return <VerifyView token={verifyToken} onSuccess={handleVerifySuccess} />;
  }

  // Show login if not authenticated
  if (!authed) {
    return <LoginView />;
  }

  // Render the current view
  function renderView() {
    switch (currentView) {
      case 'dashboard':
        return <DashboardView onNavigate={handleNavigate} />;
      case 'contacts':
        return <ContactsView onNavigate={handleNavigate} />;
      case 'contact-detail':
        return (
          <ContactDetailView
            contactId={viewParams.contactId ?? ''}
            onNavigate={handleNavigate}
          />
        );
      case 'organizations':
        return <OrganizationsView onNavigate={handleNavigate} />;
      case 'org-detail':
        return <PlaceholderView title="Organization Detail" />;
      case 'deals':
        return <DealsView onNavigate={handleNavigate} />;
      case 'deal-detail':
        return (
          <DealDetailView
            dealId={viewParams.dealId ?? ''}
            onNavigate={handleNavigate}
          />
        );
      case 'interactions':
        return <InteractionsView onNavigate={handleNavigate} />;
      case 'tasks':
        return <TasksView onNavigate={handleNavigate} />;
      case 'agenda':
        return <PlaceholderView title="Agenda" />;
      case 'dossiers':
        return <DossierView onNavigate={handleNavigate} />;
      case 'research':
        return <ResearchView onNavigate={handleNavigate} />;
      case 'reports':
        return <ReportsView onNavigate={handleNavigate} />;
      case 'workflows':
        return <WorkflowsView onNavigate={handleNavigate} />;
      case 'renewals':
        return <RenewalsView onNavigate={handleNavigate} />;
      case 'cases':
        return <CasesView onNavigate={handleNavigate} />;
      case 'playbooks':
        return <PlaybooksView onNavigate={handleNavigate} />;
      case 'email-templates':
        return <EmailTemplatesView onNavigate={handleNavigate} />;
      case 'leads':
        return <LeadsView onNavigate={handleNavigate} />;
      case 'forecasting':
        return <ForecastingView onNavigate={handleNavigate} />;
      case 'products':
        return <ProductsView onNavigate={handleNavigate} />;
      case 'quotes':
        return <QuotesView onNavigate={handleNavigate} />;
      case 'campaigns':
        return <CampaignsView onNavigate={handleNavigate} />;
      case 'sequences':
        return <SequencesView onNavigate={handleNavigate} />;
      case 'subscriptions':
        return <SubscriptionsView onNavigate={handleNavigate} />;
      case 'invoices':
        return <InvoicesView onNavigate={handleNavigate} />;
      case 'activity-queue':
        return <ActivityQueueView onNavigate={handleNavigate} />;
      case 'data-quality':
        return <DataQualityView onNavigate={handleNavigate} />;
      case 'territories':
        return <TerritoriesView onNavigate={handleNavigate} />;
      case 'web-forms':
        return <WebFormsView onNavigate={handleNavigate} />;
      case 'webhooks':
        return <WebhooksView onNavigate={handleNavigate} />;
      case 'import':
        return <ImportView onNavigate={handleNavigate} />;
      case 'tags':
        return <PlaceholderView title="Tags" />;
      case 'team':
        return <PlaceholderView title="Team" />;
      case 'search':
        return <PlaceholderView title="Search Results" />;
      case 'settings':
        return <SettingsView onNavigate={handleNavigate} />;
      default:
        return <PlaceholderView title="Not Found" />;
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar currentView={currentView} onNavigate={handleNavigate} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header onNavigate={handleNavigate} />
        {renderView()}
      </div>
    </div>
  );
}
